import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Truck, Building2, ArrowLeft, LifeBuoy } from "lucide-react";
import { APP_VERSION } from "@/lib/version";
import { isAdminLike, isSuperAdmin } from "@/lib/roles";

export default function Login() {
  const { login, verify2FA, switchEmpresa, logout, tenantBranding, startImpersonate } = useAuth();
  const nav = useNavigate();
  const { slug: urlSlug } = useParams();
  const isRoot = !urlSlug;
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials"); // credentials | totp | select-empresa
  const [empresasList, setEmpresasList] = useState([]);
  const [loggedUser, setLoggedUser] = useState(null);
  const [totpCode, setTotpCode] = useState("");

  // Branding do tenant carregado por TenantBrandingSync quando estamos em /:slug/login.
  const brandCor = tenantBranding?.cor_primaria || "#EA580C";
  const brandNome = tenantBranding?.nome || "Controle de Entregas";
  const brandTagline = tenantBranding?.tagline || "Gestão de entregas para farmácias e drogarias.";
  const brandSegmento = tenantBranding?.segmento;

  const goToApp = (u) => {
    // Entregador vai direto para a interface mobile
    if (u?.entregador_id || u?.role === "entregador") { nav("/mobile", { replace: true }); return; }
    const isSA = u?.role === "super_admin" || (u?.role === "admin" && !(u?.empresa_ids || []).length);
    // Super Admin no login raiz → Painel Global
    if (isSA && isRoot) { nav("/admin/empresas", { replace: true }); return; }
    // Se estamos num /:slug/login, entra no dashboard daquele tenant
    if (urlSlug) { nav(`/${urlSlug}/`, { replace: true }); return; }
    nav("/", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const resp = await login(email, senha);
      // Desafio 2FA (SA com autenticador ativo): mostra passo TOTP e não avança
      if (resp?.requires_2fa) {
        setStep("totp");
        setTotpCode("");
        toast.info("Digite o código do seu app autenticador");
        return;
      }
      const { user: u, empresas: list, redirect_slug } = resp;
      if (u?.entregador_id || u?.role === "entregador") { goToApp(u); return; }
      const isSA = u?.role === "super_admin" || (u?.role === "admin" && !(u?.empresa_ids || []).length);
      // Login RAIZ (usegestao.app.br/login):
      // - tenant user com slug único → redireciona pra /{slug}/
      // - super admin → picker (com opção Painel Global no topo)
      if (isRoot && redirect_slug && !isSA) {
        toast.success(`Entrando em ${redirect_slug}...`);
        nav(`/${redirect_slug}/`, { replace: true });
        return;
      }
      if (isRoot && !isSA) {
        if (list.length === 1 && list[0].slug) {
          nav(`/${list[0].slug}/`, { replace: true });
          return;
        }
        if (list.length > 1) {
          setEmpresasList(list); setLoggedUser(u); setStep("select-empresa");
          return;
        }
        toast.error("Sua conta não está vinculada a nenhuma empresa. Contate o administrador.");
        return;
      }
      // Super Admin no login raiz → mostra picker (Painel Global + Empresas)
      if (isSA && isRoot) {
        setEmpresasList(list); setLoggedUser(u); setStep("select-empresa");
        return;
      }
      // Super Admin no /:slug/login → entra direto naquele tenant
      if (isSA) { goToApp(u); return; }
      // Tenant login (/:slug/login) para usuário normal: só picker se tiver múltiplas empresas + perm
      const canSelect = (u?.permissions || []).includes("select_empresa");
      if (!canSelect || list.length <= 1) { goToApp(u); return; }
      setEmpresasList(list); setLoggedUser(u); setStep("select-empresa");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao entrar");
    } finally { setLoading(false); }
  };

  const escolherEmpresa = (id) => {
    const emp = empresasList.find((e) => e.id === id);
    const isSA = loggedUser?.role === "super_admin" || (loggedUser?.role === "admin" && !(loggedUser?.empresa_ids || []).length);
    switchEmpresa(id);
    // Todos vão para /{slug}/ da empresa escolhida (super admin ou tenant user)
    if (emp?.slug) { nav(`/${emp.slug}/`, { replace: true }); return; }
    if (isSA) { nav("/admin/empresas", { replace: true }); return; }
    nav("/", { replace: true });
  };

  const irPainelGlobal = () => {
    nav("/admin/empresas", { replace: true });
  };

  const acessarSuporte = async (empresa) => {
    try {
      await startImpersonate(empresa.id);
      toast.success(`Modo Suporte ativado para "${empresa.nome}"`);
      nav(`/${empresa.slug}/`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao iniciar Modo Suporte");
    }
  };

  const voltar = () => { logout(); setStep("credentials"); setEmpresasList([]); setLoggedUser(null); setTotpCode(""); };

  const submitTotp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(totpCode)) { toast.error("Informe o código de 6 dígitos"); return; }
    setLoading(true);
    try {
      const { user: u, empresas: list } = await verify2FA(totpCode);
      const isSA = u?.role === "super_admin" || (u?.role === "admin" && !(u?.empresa_ids || []).length);
      if (isSA && isRoot) {
        setEmpresasList(list); setLoggedUser(u); setStep("select-empresa");
        return;
      }
      goToApp(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código incorreto");
      setTotpCode("");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-white p-4">
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center">
        <div className="hidden md:block">
          <div className="flex items-center gap-3 mb-6">
            {tenantBranding?.logo_base64
              ? <img src={tenantBranding.logo_base64} alt="logo" data-testid="brand-logo" className="w-14 h-14 object-contain rounded-lg bg-white border border-orange-100 p-1"/>
              : <Truck className="w-10 h-10" style={{ color: brandCor }}/>}
            <span className="text-3xl font-extrabold tracking-tight" data-testid="brand-nome">{brandNome}</span>
          </div>
          <h1 className="text-5xl font-extrabold leading-tight mb-4" data-testid="brand-tagline">
            {brandTagline}
          </h1>
          <p className="text-muted-foreground text-lg">
            {brandSegmento
              ? <>Segmento: <b>{brandSegmento}</b> · Cadastre entregadores, controle status em tempo real, calcule troco automaticamente e gere relatórios em PDF/CSV.</>
              : "Cadastre entregadores, controle status em tempo real, calcule troco automaticamente e gere relatórios em PDF/CSV."}
          </p>
        </div>

        {step === "credentials" ? (
          <Card className="shadow-xl border-orange-100" data-testid="login-card">
            <CardHeader><CardTitle className="text-2xl">Entrar</CardTitle></CardHeader>
            <CardContent>
              {urlSlug === "demo" && (
                <div className="mb-4 p-3 rounded-lg border-2 border-purple-300 bg-purple-50">
                  <div className="text-sm font-semibold text-purple-900 mb-2">Ambiente de Demonstração</div>
                  <p className="text-xs text-purple-800 mb-3">Você não precisa criar conta. Clique abaixo para explorar o sistema com dados fictícios.</p>
                  <Button
                    type="button"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="btn-demo-nologin"
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const { api } = await import("@/lib/api");
                        await api.post("/auth/demo-login");
                        toast.success("Entrando no Ambiente Demo…");
                        nav("/demo/", { replace: true });
                        // recarrega pra AuthContext detectar cookie novo
                        window.location.reload();
                      } catch (e) {
                        console.error("Falha ao entrar no Ambiente Demo", e);
                        toast.error(e?.response?.data?.detail || "Ambiente Demo indisponível");
                      }
                      finally { setLoading(false); }
                    }}
                  >
                    Ver Demonstração (sem login)
                  </Button>
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div><Label htmlFor="email">E-mail ou nome de usuário</Label>
                  <Input data-testid="login-email" id="email" type="text" inputMode="text" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" placeholder="seu@email.com ou seu usuário"/>
                  <p className="text-[11px] text-muted-foreground mt-1">Você pode entrar com seu <b>e-mail</b>, <b>nome</b> ou <b>nome de usuário</b>.</p></div>
                <div><Label htmlFor="senha">Senha</Label>
                  <Input data-testid="login-senha" id="senha" type="password" value={senha} onChange={(e) => setSenha(e.target.value)} required autoComplete="current-password" /></div>
                <Button data-testid="login-submit" type="submit" disabled={loading}
                  className="w-full text-white hover:opacity-90"
                  style={{ backgroundColor: brandCor }}>
                  {loading ? "Entrando..." : "Entrar"}</Button>
                <button type="button" onClick={() => toast.info("Esqueceu a senha? Peça ao administrador para gerar um link de recuperação em Usuários → Gerar link. Você poderá redefinir sua senha em seguida.", { duration: 8000 })}
                  className="text-xs text-orange-600 hover:underline w-full text-center" data-testid="link-forgot">
                  Esqueci minha senha
                </button>
              </form>
              <div className="mt-4 pt-3 border-t border-orange-100 text-center">
                <span className="text-[10px] text-muted-foreground font-mono tracking-wide" data-testid="login-version">
                  {APP_VERSION}
                </span>
              </div>
            </CardContent>
          </Card>
        ) : step === "totp" ? (
          <Card className="shadow-xl border-orange-100" data-testid="totp-card">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Truck className="w-6 h-6" style={{ color: brandCor }} />
                Verificação em 2 etapas
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Digite o código de 6 dígitos do seu app autenticador (Google Authenticator, Authy, 1Password…).
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitTotp} className="space-y-4">
                <div>
                  <Label htmlFor="totp">Código do autenticador</Label>
                  <Input
                    id="totp"
                    data-testid="login-totp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    placeholder="123456"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="font-mono text-xl tracking-widest text-center h-12"
                    autoFocus
                    required
                  />
                </div>
                <Button
                  data-testid="login-totp-submit"
                  type="submit"
                  disabled={loading || totpCode.length !== 6}
                  className="w-full text-white hover:opacity-90"
                  style={{ backgroundColor: brandCor }}
                >
                  {loading ? "Verificando..." : "Confirmar"}
                </Button>
                <button type="button" onClick={voltar}
                  className="text-xs text-slate-500 hover:underline w-full text-center" data-testid="totp-cancel">
                  ← Voltar para o login
                </button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-xl border-orange-100" data-testid="empresa-select-card">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Building2 className="w-6 h-6 text-orange-600"/>Escolha a empresa
              </CardTitle>
              <p className="text-sm text-muted-foreground">Olá, <b>{loggedUser?.nome}</b> · você tem acesso a {empresasList.length} empresas</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {isSuperAdmin(loggedUser) && (
                <>
                  <button
                    onClick={irPainelGlobal}
                    data-testid="login-painel-global"
                    className="w-full flex items-center gap-3 p-3 border-2 border-indigo-200 hover:border-indigo-500 hover:bg-indigo-50 rounded-lg transition text-left mb-1"
                  >
                    <div className="w-12 h-12 rounded bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white shrink-0">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate text-indigo-900">Painel Global · SaaS</div>
                      <div className="text-xs text-indigo-700/70 truncate">
                        Gerenciar todas as empresas · sem entrar em nenhuma
                      </div>
                    </div>
                    <span className="text-indigo-600 font-bold text-sm">Entrar →</span>
                  </button>
                  <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2" data-testid="suporte-hint">
                    <LifeBuoy className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <span className="text-amber-800">
                      <b>Ou entre em uma empresa específica:</b> clique no card para entrar como Super Admin, ou no ícone <LifeBuoy className="inline w-3 h-3 mx-0.5" /> para ativar <b>Modo Suporte</b> (impersona como admin da empresa).
                    </span>
                  </div>
                </>
              )}
              {empresasList.map((e) => (
                <div key={e.id} className="flex items-stretch gap-1">
                  <button onClick={() => escolherEmpresa(e.id)} data-testid={`login-empresa-${e.id}`}
                    className="flex-1 flex items-center gap-3 p-3 border-2 border-orange-100 hover:border-orange-500 hover:bg-orange-50 rounded-lg transition text-left">
                    {e.logo_base64
                      ? <img src={e.logo_base64} alt="" className="w-12 h-12 object-contain rounded bg-white shrink-0"/>
                      : <div className="w-12 h-12 rounded bg-orange-100 flex items-center justify-center text-orange-600 shrink-0"><Building2 className="w-6 h-6"/></div>}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{e.nome}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {e.cnpj || "—"}{e.segmento ? ` · ${e.segmento}` : ""}
                      </div>
                    </div>
                    <span className="text-orange-600 font-bold text-sm">Entrar →</span>
                  </button>
                  {isSuperAdmin(loggedUser) && (
                    <button onClick={() => acessarSuporte(e)}
                      data-testid={`login-suporte-${e.id}`}
                      title={`Modo Suporte em "${e.nome}" — entra como admin sem aparecer na lista de usuários`}
                      className="w-14 shrink-0 border-2 border-amber-200 hover:border-amber-500 hover:bg-amber-50 rounded-lg transition flex flex-col items-center justify-center gap-0.5 text-amber-700">
                      <LifeBuoy className="w-5 h-5"/>
                      <span className="text-[9px] font-bold uppercase leading-none">Suporte</span>
                    </button>
                  )}
                </div>
              ))}
              <Button variant="ghost" onClick={voltar} className="w-full mt-3 text-muted-foreground" data-testid="login-voltar">
                <ArrowLeft className="w-4 h-4 mr-2"/>Voltar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Rodapé LGPD — fev/2026: visível em todos os estados do Login.
            Sem target="_blank" pra evitar popup blockers em mobile/PWA — a página
            /termos tem botão "Voltar" nativo pra retornar. */}
        <div className="mt-6 text-center text-xs text-slate-500 space-y-1" data-testid="login-footer">
          <div className="flex items-center justify-center gap-3">
            <a
              href="/termos"
              className="hover:text-orange-700 hover:underline"
              data-testid="login-link-termos"
            >
              Termos de Uso & LGPD
            </a>
            <span className="text-slate-300">·</span>
            <a
              href="/privacidade"
              className="hover:text-orange-700 hover:underline"
              data-testid="login-link-privacidade"
            >
              Política de Privacidade
            </a>
          </div>
          <div>© {new Date().getFullYear()} Gestão de Entregas</div>
        </div>
      </div>
    </div>
  );
}
